import { logger } from '@/core/logger';
import { asyncStorage } from '@/core/storage/asyncStorage';

/**
 * Storage keys for registration state
 */
const STORAGE_KEYS = {
    HAS_REACHED_REGISTRATION_SUCCESS: 'registration.hasReachedSuccess',
    REGISTRATION_IDS: 'registration.ids', // Store registration IDs separately for immediate access
    REGISTRATION_STATUS: 'registration.status', // Store approved status to avoid redundant API calls
    REGISTRATION_TYPE: 'registration.type', // Store registration type (manual vs uae-pass) to show correct button after data is cleared
} as const;

/**
 * Registration IDs structure for storage
 */
export interface RegistrationIds {
    registrationId: string;
    customerId: string;
    idn: string; // Emirates ID (for UAE users)
    passportNumber?: string; // Passport number (for international/passport-only users)
    nationality: string; // Nationality (e.g., "Emirati", "Indian")
}


/**
 * Registration storage management
 * Handles storage of registration flow state
 */
export class RegistrationStorage {
    /**
     * Check if user has reached registration success screen (for UAE Pass flow)
     */
    static async hasReachedRegistrationSuccess(): Promise<boolean> {
        try {
            const value = await asyncStorage.getItem(STORAGE_KEYS.HAS_REACHED_REGISTRATION_SUCCESS);
            return value === 'true';
        } catch (error) {
            logger.error('RegistrationStorage:Failed to check registration success state', error);
            return false;
        }
    }

    /**
     * Mark that user has reached registration success screen
     */
    static async setReachedRegistrationSuccess(): Promise<void> {
        try {
            await asyncStorage.setItem(STORAGE_KEYS.HAS_REACHED_REGISTRATION_SUCCESS, 'true');
            logger.info('RegistrationStorage:Registration success state saved');
        } catch (error) {
            logger.error('RegistrationStorage:Failed to save registration success state', error);
            throw error;
        }
    }

    /**
     * Clear registration success state (e.g., on logout or successful login)
     */
    static async clearReachedRegistrationSuccess(): Promise<void> {
        try {
            await asyncStorage.removeItem(STORAGE_KEYS.HAS_REACHED_REGISTRATION_SUCCESS);
            logger.info('RegistrationStorage:Registration success state cleared');
        } catch (error) {
            logger.error('RegistrationStorage:Failed to clear registration success state', error);
            throw error;
        }
    }

    /**
     * Save registration IDs for status API calls
     */
    static async setRegistrationIds(ids: RegistrationIds): Promise<void> {
        try {
            await asyncStorage.setObject(STORAGE_KEYS.REGISTRATION_IDS, ids);
            logger.info('RegistrationStorage:Registration IDs saved', {
                hasRegistrationId: !!ids.registrationId,
                hasCustomerId: !!ids.customerId,
                hasIdn: !!ids.idn,
                hasPassportNumber: !!ids.passportNumber,
                hasNationality: !!ids.nationality,
            });
        } catch (error) {
            logger.error('RegistrationStorage:Failed to save registration IDs', error);
            throw error;
        }
    }

    /**
     * Get stored registration IDs
     */
    static async getRegistrationIds(): Promise<RegistrationIds | null> {
        try {
            const ids = await asyncStorage.getObject<RegistrationIds>(STORAGE_KEYS.REGISTRATION_IDS);
            logger.debug('RegistrationStorage:Retrieved registration IDs', { hasIds: !!ids });
            return ids;
        } catch (error) {
            logger.error('RegistrationStorage:Failed to retrieve registration IDs', error);
            return null;
        }
    }

    /**
     * Clear registration IDs (e.g., on logout or successful login)
     */
    static async clearRegistrationIds(): Promise<void> {
        try {
            await asyncStorage.removeItem(STORAGE_KEYS.REGISTRATION_IDS);
            logger.info('RegistrationStorage:Registration IDs cleared');
        } catch (error) {
            logger.error('RegistrationStorage:Failed to clear registration IDs', error);
            throw error;
        }
    }

    /**
     * Save registration status (e.g., 'approved') with registration_id to avoid redundant API calls
     * The registration_id is used to validate that cached status belongs to current registration
     */
    static async setRegistrationStatus(status: string, registrationId: string): Promise<void> {
        try {
            const statusData: { status: string; registrationId: string } = {
                status,
                registrationId,
            };
            await asyncStorage.setObject(STORAGE_KEYS.REGISTRATION_STATUS, statusData);
            logger.info('RegistrationStorage:Registration status saved', { status, registrationId });
        } catch (error) {
            logger.error('RegistrationStorage:Failed to save registration status', error);
            throw error;
        }
    }

    /**
     * Get stored registration status with registration_id validation
     * Returns status only if registration_id matches current registration
     */
    static async getRegistrationStatus(currentRegistrationId?: string | null): Promise<string | null> {
        try {
            const statusData = await asyncStorage.getObject<{ status: string; registrationId: string }>(STORAGE_KEYS.REGISTRATION_STATUS);
            if (!statusData) {
                logger.debug('RegistrationStorage:No stored registration status found');
                return null;
            }

            // If no current registration_id provided, return status (backward compatibility)
            if (!currentRegistrationId) {
                logger.debug('RegistrationStorage:Retrieved registration status (no validation)', { 
                    hasStatus: !!statusData.status,
                    storedRegistrationId: statusData.registrationId,
                });
                return statusData.status;
            }

            // Validate that stored status belongs to current registration
            if (statusData.registrationId === currentRegistrationId) {
                logger.debug('RegistrationStorage:Retrieved registration status (validated)', { 
                    status: statusData.status,
                    registrationId: currentRegistrationId,
                });
                return statusData.status;
            } else {
                logger.warn('RegistrationStorage:Stored status belongs to different registration, ignoring', {
                    storedRegistrationId: statusData.registrationId,
                    currentRegistrationId,
                    storedStatus: statusData.status,
                });
                // Clear invalid status
                await this.clearRegistrationStatus();
                return null;
            }
        } catch (error) {
            logger.error('RegistrationStorage:Failed to retrieve registration status', error);
            return null;
        }
    }

    /**
     * Clear registration status (e.g., on logout or successful login)
     */
    static async clearRegistrationStatus(): Promise<void> {
        try {
            await asyncStorage.removeItem(STORAGE_KEYS.REGISTRATION_STATUS);
            logger.info('RegistrationStorage:Registration status cleared');
        } catch (error) {
            logger.error('RegistrationStorage:Failed to clear registration status', error);
            throw error;
        }
    }

    /**
     * Save registration type (manual vs uae-pass) to show correct button after data is cleared
     */
    static async setRegistrationType(type: 'manual' | 'uae-pass'): Promise<void> {
        try {
            await asyncStorage.setItem(STORAGE_KEYS.REGISTRATION_TYPE, type);
            logger.info('RegistrationStorage:Registration type saved', { type });
        } catch (error) {
            logger.error('RegistrationStorage:Failed to save registration type', error);
            throw error;
        }
    }

    /**
     * Get stored registration type
     */
    static async getRegistrationType(): Promise<'manual' | 'uae-pass' | null> {
        try {
            const type = await asyncStorage.getItem(STORAGE_KEYS.REGISTRATION_TYPE);
            logger.debug('RegistrationStorage:Retrieved registration type', { hasType: !!type, type });
            return type as 'manual' | 'uae-pass' | null;
        } catch (error) {
            logger.error('RegistrationStorage:Failed to retrieve registration type', error);
            return null;
        }
    }

    /**
     * Clear registration type
     */
    static async clearRegistrationType(): Promise<void> {
        try {
            await asyncStorage.removeItem(STORAGE_KEYS.REGISTRATION_TYPE);
            logger.info('RegistrationStorage:Registration type cleared');
        } catch (error) {
            logger.error('RegistrationStorage:Failed to clear registration type', error);
            throw error;
        }
    }

    /**
     * Clear all registration data (status, IDs, success state, type)
     */
    static async clearAll(): Promise<void> {
        try {
            await Promise.all([
                this.clearRegistrationStatus(),
                this.clearRegistrationIds(),
                this.clearReachedRegistrationSuccess(),
                this.clearRegistrationType(),
            ]);
            logger.info('RegistrationStorage:All registration data cleared');
        } catch (error) {
            logger.error('RegistrationStorage:Failed to clear all registration data', error);
            throw error;
        }
    }
}

